import React from "react";
import { Link } from "react-router-dom";
import { Compass, Eye, Heart, Target, Users, ShieldAlert, Sparkles, CheckCircle } from "lucide-react";

export default function About() {
  const visionPillars = [
    { title: "Emotionally Aware Individuals", desc: "Equipping people with somatic tools to understand their reactivity and find choice." },
    { title: "Conscious Relationships", desc: "Enabling couples and families to dissolve protective defenses and connect safely." },
    { title: "Ethical Abundance Creation", desc: "Encouraging individuals to build wealth from a posture of purpose and ecological safety." },
    { title: "Environmental Stewardship", desc: "Fostering active care and physical responsibility for nature and ecological systems." }
  ];

  const missionPillars = [
    { num: "01", title: "Teachings", desc: "Make deep somatic and emotional transformational wisdom highly practical and accessible to everyone." },
    { num: "02", title: "Emotional Support", desc: "Create safe, structured, and compassionate communities where individuals never have to grow in isolation." },
    { num: "03", title: "Systemic Service", desc: "Build systems of outer contribution that elevate the fundamental dignity, wellness, and care of communities." },
    { num: "04", title: "Somatic Freedom", desc: "Promote nervous-system down-regulation and deep self-love as standard practices for conscious living." }
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#050505] text-[#F6F4EE] relative" data-testid="about-page">
      <div className="absolute inset-0 glow-radial opacity-20 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        {/* SECTION 1: ABOUT ESHA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-28 items-center">
          <div className="lg:col-span-7">
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#00D2FF] px-3 py-1.5 border border-[#00D2FF]/20 rounded-full bg-[#111111]/80 font-bold">
              The Foundation
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-[#F6F4EE] leading-tight mt-6 mb-6">
              ESHA is a journey <span className="text-blue-shimmer italic">back to yourself.</span>
            </h1>
            <p className="font-sans text-base sm:text-lg text-[#AAA29E] leading-relaxed mb-6">
              Eternal Soul Healing & Awakening (ESHA) exists to make profound emotional intelligence, conscious relationship skills, self-love, somatic nervous-system regulation, and aligned abundance highly accessible to modern seekers.
            </p>
            <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed mb-8">
              We do not promote superficial mindset hacks or quick-fix spiritual bypassing. Instead, ESHA integrates structured learning, physiological regulation, conscious community support, and practical action to help individuals build their lives from a baseline of absolute choice and peace.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#0F0F0F]/80 border border-[#FF8C00]/15 rounded-xl shadow-md">
                <h4 className="font-serif text-[#00D2FF] font-semibold text-sm font-bold">Psychological Awareness</h4>
                <p className="text-xs text-[#AAA29E] mt-1">Understanding subconscious defensive mechanisms.</p>
              </div>
              <div className="p-4 bg-[#0F0F0F]/80 border border-[#FF8C00]/15 rounded-xl shadow-md">
                <h4 className="font-serif text-[#FF8C00] font-semibold text-sm font-bold">Somatic Regulation</h4>
                <p className="text-xs text-[#AAA29E] mt-1">Physical practices to settle somatic triggers.</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative border border-[#FF8C00]/15 rounded-2xl overflow-hidden aspect-[4/5] shadow-lg bg-[#0F0F0F]">
              <img
                src="/assets/eswari-photo.jpg"
                alt="Eswari Podugu beside a glowing golden lotus — the spirit of ESHA"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
          </div>
        </div>

        {/* SECTION 2: MEET ESWARI */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-28 items-center">
          <div className="lg:col-span-5 order-last lg:order-first">
            <div className="relative border border-[#FF8C00]/15 rounded-2xl overflow-hidden aspect-[4/5] shadow-lg bg-[#0F0F0F]">
              <img
                src="/assets/eswari-founder.png"
                alt="Eswari Podugu — Founder of ESHA, Eternal Soul Healing and Awakening"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#C87D53] px-3 py-1.5 border border-[#C87D53]/15 rounded-full bg-[#111111]/80 font-bold">
              The Founder
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-[#F6F4EE] leading-tight mt-6 mb-6">
              About Eswari Podugu
            </h2>
            <p className="font-sans text-base text-[#AAA29E] leading-relaxed mb-6">
              Eswari&apos;s journey has been forged through the fire of intense real-life challenges, honest self-discovery, years of persistent learning, deep healing, and total physiological transformation.
            </p>
            <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed mb-6">
              She explored emotional awareness, somatic nervous-system work, and energy alignment not as conceptual hobbies, but as critical survival protocols for rebuilding the entire foundation of how she related to herself, her family, and her outer life.
            </p>
            <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed mb-8">
              Her focus remains simple and direct: Teachings must be practical, results must be observable in daily relationships, and students must be fully empowered to hold their own emotional sovereignty.
            </p>

            <blockquote className="border-l-2 border-[#00D2FF] pl-6 italic font-serif text-lg text-[#F6F4EE]/95 mb-8 max-w-2xl">
              “If awareness can change one life, it can become a system that helps many lives. You do not need to become perfect. You only need to become conscious.”
            </blockquote>

            <div className="flex gap-4">
              <a
                href="https://learn.eshawitheswari.com/web/lite/events/6a48df41599e3232f7dee506"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3.5 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full hover:scale-105 active:scale-95 transition-all shadow-md"
                data-testid="about-eswari-clarity-call-btn"
              >
                Apply for Clarity Call
              </a>
            </div>
          </div>
        </div>

        {/* SECTION 3: VISION & MISSION */}
        <div className="border-t border-[#FF8C00]/15 pt-20 mb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Vision */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-[#00D2FF]/10 flex items-center justify-center border border-[#00D2FF]/15">
                  <Eye className="h-5 w-5 text-[#00D2FF]" />
                </div>
                <h3 className="font-serif text-3xl text-[#F6F4EE] font-light">ESHA Vision</h3>
              </div>
              <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed mb-8">
                We envision a world where emotional intelligence and somatic awareness are not premium luxuries, but standard life practices. Our vision is to empower individuals to break inherited ancestral trauma patterns, constructing families of deep relational safety and absolute mutual respect.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {visionPillars.map((p, idx) => (
                  <div key={idx} className="p-5 bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl hover:border-[#00D2FF]/40 transition-colors shadow-sm">
                    <h4 className="font-serif text-sm font-bold text-[#F6F4EE] mb-1">{p.title}</h4>
                    <p className="font-sans text-xs text-[#AAA29E] leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mission */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-[#C87D53]/10 flex items-center justify-center border border-[#C87D53]/15">
                  <Target className="h-5 w-5 text-[#C87D53]" />
                </div>
                <h3 className="font-serif text-3xl text-[#F6F4EE] font-light">ESHA Mission</h3>
              </div>
              <p className="font-sans text-sm sm:text-base text-[#AAA29E] leading-relaxed mb-8">
                Teach. Transform. Support. Serve. We establish high-fidelity educational platforms, somatic inquiry circles, and dedicated contribution models to uplift the health, self-respect, and material dignity of those we are privileged to serve.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {missionPillars.map((p, idx) => (
                  <div key={idx} className="p-5 bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-xl hover:border-[#FF8C00]/40 transition-colors shadow-sm">
                    <div className="font-mono text-xs font-bold text-[#00D2FF] uppercase tracking-widest mb-1">Pillar {p.num}</div>
                    <h4 className="font-serif text-sm font-bold text-[#F6F4EE] mb-1">{p.title}</h4>
                    <p className="font-sans text-xs text-[#AAA29E] leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 4: COMMUNITY */}
        <div className="bg-[#0F0F0F] border border-[#FF8C00]/15 rounded-3xl p-8 sm:p-16 text-center relative overflow-hidden shadow-lg">
          <div className="absolute inset-0 glow-radial opacity-25" />
          <div className="relative z-10 max-w-3xl mx-auto">
            <Users className="h-10 w-10 text-[#00D2FF] mx-auto mb-6" />
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F6F4EE] font-light mb-6">
              The ESHA Community
            </h2>
            <p className="font-sans text-base text-[#AAA29E] leading-relaxed mb-8">
              True growth is sustained in circles of shared intention. The ESHA Sovereign Community brings together thousands of dedicated students, parents, couples, and leaders from around the world to practice somatic inquiry, boundary-honoring, and conscious vulnerability in a container of absolute safety and mutual care.
            </p>
            <Link
              to="/offerings"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#FF8C00] to-[#FF4500] text-white font-mono text-xs tracking-widest uppercase font-bold rounded-full transition-all duration-300 shadow-md"
              data-testid="about-community-cta-btn"
            >
              Explore ESHA Community Offerings
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
